/**
 * Host-theme adaptation.
 *
 * The configurator runs in a CROSS-ORIGIN iframe, so it can't read the merchant's
 * page styles itself. The embed snippet (neovaa-embed.js) runs ON the merchant's
 * page (same-origin there), reads the store's real background / text colour / font /
 * button radius, and passes them to this iframe as URL params. We apply them as
 * token overrides so the configurator blends into whatever store it's dropped into,
 * white or black, store font, store button shape, with zero per-client setup.
 */

export interface HostTheme {
  bg?: string;
  fg?: string;
  font?: string;
  radius?: string;
  accent?: string;
  /** Per-client layout variant (manual override only). "unified" = the option groups sit
   *  on ONE continuous card with no gaps; default = separate glass tiles. */
  layout?: string;
  /** Self-hosted font files for `font` (brand kits: Euclid, CaslonDoric, SanomatSans…
   *  are NOT on Google Fonts). Each entry becomes an injected @font-face; when present,
   *  the Google Fonts fallback fetch is skipped. URLs are same-origin paths
   *  (e.g. /brand-fonts/koala/euclid-circular-b-400-regular.woff2). */
  fontFaces?: Array<{ family: string; url: string; weight?: number; style?: string }>;
  /** Micro-shape brand tokens (Alex 2026-06-12: every little shape should follow the
   *  brand). Each maps to a CSS var consumed with the Neovaa default as fallback. */
  /** Big surfaces: viewer card, menu panel, modal sheet, part-detail card. */
  radiusCard?: string;
  /** Small controls: shape tiles, size segments, menu items, dimension labels. */
  radiusControl?: string;
  /** Colorway swatch chips (the COLORS stay ours; the chip shape follows the brand). */
  radiusChip?: string;
  /** CTA typography: "uppercase" (Castlery) or "none"/absent (sentence case). */
  ctaCase?: string;
  /** CTA letter-spacing (Castlery 0.2em). */
  ctaLetterSpacing?: string;
  /** Display font for the BIG typography (product name, CTA title, logo) — where a
   *  brand's voice actually shows (Castlery = Aime serif, Loaf = CaslonDoric). Falls
   *  back to `font`/inherit when absent. */
  displayFont?: string;
  /** Selection state color (selected tile/segment border + tint, swatch contour).
   *  Defaults to `accent` when absent — never stays the Neovaa ink. */
  selectColor?: string;
  /** CTA text color (Castlery #F6F3E7 on burnt orange). */
  accentText?: string;
  /** Small floating pills (the viewer Menu button). Pill brands keep 999px;
   *  square brands (Castlery 8px, Loaf 5px) get their corner. */
  radiusPill?: string;
  /** Brand logo URL (consumed by the UI header + HD loader, not by tokens). */
  logoUrl?: string;
  /** Structural skin: loads /brand-skins/<id>.css and tags body[data-nv-skin]. */
  brandSkin?: string;
  /** Standalone-demo brand chrome (promo strip + nav). Consumed by the UI, not tokens. */
  chrome?: { promo?: string; promoBg?: string; promoFg?: string; nav?: string[]; saleLabel?: string };
  /** Per-brand buy-row layout (consumed by the UI). */
  ctaLabel?: string;
  ctaSplit?: boolean;
  hideRailPrice?: boolean;
  reviewsBelowBuy?: boolean;
  trustBadges?: string[];
}

/** Read the theme the embed snippet passed in the URL, or null if none. */
export function getHostTheme(): HostTheme | null {
  const p = new URLSearchParams(location.search);
  const t: HostTheme = {
    bg: p.get("bg") || undefined,
    fg: p.get("fg") || undefined,
    font: p.get("font") || undefined,
    radius: p.get("radius") || undefined,
  };
  return t.bg || t.fg || t.font || t.radius ? t : null;
}

/** Override the design tokens + body background + font with the host's.
 *  `source` distinguishes the two callers: "auto" = inherited from the merchant's store CSS
 *  (radius read from a BUTTON — must be capped so it can't arch a card); "manual" = a
 *  deliberate Control Room Look the designer sees in live preview (radius applied verbatim). */
export function applyHostTheme(t: HostTheme, source: "auto" | "manual" = "auto"): void {
  // Set on BOTH :root and <body>. The mobile themes (themes.css) set --nv-bg / --nv-sans
  // on body[data-mobile][data-nv-theme], and the cascade applies that to everything inside
  // <body> — so a :root-only override loses. An inline style on <body> beats that rule.
  const targets = [document.documentElement, document.body].filter(Boolean) as HTMLElement[];
  const set = (k: string, v: string) => targets.forEach((el) => el.style.setProperty(k, v));

  if (t.bg) {
    set("--nv-bg", t.bg);
    set("--nv-bg-page", t.bg);
    set("--nv-bg-soft", t.bg);
    set("--nv-viewer-bg", t.bg); // 3D hero stage backdrop
    targets.forEach((el) => { el.style.background = t.bg as string; });
  }
  if (t.fg) {
    set("--nv-fg", t.fg);
    set("--nv-select", t.fg);
  }
  if (t.radius) {
    // The host radius is read from a BUTTON (neovaa-embed.js reads --buttons-radius or a
    // real button's border-radius). A store button is often a full PILL — 40px+, or a "%"
    // / large value that resolves to ~half the element. That shape is correct for OUR buy
    // button, but the SAME token used to drive WIDE structural cards (the unified
    // .mob-steps options card, tiles, popovers — all `var(--nv-radius)`, a crisp 3px by
    // design) turns each card into a giant rounded "arch" and pushes the options down.
    // So: the CTA mirrors the store's true button shape; the structural tokens only pick up
    // GENTLE host rounding, hard-capped so a wide card can never become an arch.
    set("--nv-cta-radius", t.radius);
    set("--nv-radius", source === "auto" ? clampRadius(t.radius, 8) : t.radius);
    set("--nv-radius-sm", source === "auto" ? clampRadius(t.radius, 6) : t.radius);
  }
  if (t.accent) {
    set("--nv-accent", t.accent);
    set("--nv-cta-bg", t.accent);
  }
  // Selection state follows the brand: explicit selectColor, else the accent.
  // color-mix keeps the fill a soft tint of the same hue (no hand-made rgba).
  const sel = t.selectColor ?? t.accent;
  if (sel) {
    set("--nv-sel-border", sel);
    set("--nv-sel-fill", `color-mix(in srgb, ${sel} 10%, transparent)`);
  }
  if (t.accentText) set("--nv-cta-fg", t.accentText);
  if (t.displayFont) {
    set("--nv-display", t.displayFont);
    // A separate display family (e.g. Neovaa's Montserrat) also needs fetching
    // when it isn't one of the self-hosted brand faces.
    if (!t.fontFaces || t.fontFaces.length === 0) loadGoogleFont(t.displayFont);
  }
  if (t.radiusPill) set("--nv-r-pill", t.radiusPill);
  if (t.radiusCard) set("--nv-r-card", t.radiusCard);
  if (t.radiusControl) set("--nv-r-control", t.radiusControl);
  if (t.radiusChip) set("--nv-r-chip", t.radiusChip);
  if (t.ctaCase) set("--nv-cta-case", t.ctaCase);
  if (t.ctaLetterSpacing) set("--nv-cta-ls", t.ctaLetterSpacing);
  if (t.fontFaces && t.fontFaces.length > 0) injectFontFaces(t.fontFaces);
  if (t.font) {
    set("--nv-sans", t.font);
    set("--nv-serif", t.font); // store fonts rarely pair a serif; match everything to it
    if (!t.fontFaces || t.fontFaces.length === 0) loadGoogleFont(t.font);
  }
  if (t.layout && document.body) {
    document.body.dataset.nvLayout = t.layout; // CSS keys the one-card layout off this
  }
  if (t.brandSkin) {
    if (document.body) document.body.dataset.nvSkin = t.brandSkin;
    if (!document.getElementById("nv-brand-skin")) {
      const link = document.createElement("link");
      link.id = "nv-brand-skin";
      link.rel = "stylesheet";
      link.href = `/brand-skins/${encodeURIComponent(t.brandSkin)}.css`;
      document.head.appendChild(link);
    }
  }
}

/** Clamp a host CSS radius to a px maximum. A button's radius can be a pill: a large px,
 *  or a "%"/keyword/rem that would resolve huge on a wide card. Anything that isn't a small
 *  px length collapses to the cap, so a structural card can never inherit a button's pill
 *  shape and arch. A genuinely small store radius (e.g. 6px sharp-ish buttons) passes
 *  through, keeping the blend-in the host-theme system is for. */
function clampRadius(radius: string, maxPx: number): string {
  const m = /(-?\d*\.?\d+)px/.exec(radius);
  if (!m) return `${maxPx}px`; // %, rem, keyword, multi-value pill → not a plain px → clamp
  return `${Math.min(parseFloat(m[1]), maxPx)}px`;
}

/** Inject @font-face rules for self-hosted brand fonts (idempotent per URL set).
 *  woff2 URLs are same-origin; `font-display: swap` keeps text visible while
 *  the brand glyphs stream in. */
function injectFontFaces(faces: NonNullable<HostTheme["fontFaces"]>): void {
  const id = "nv-brand-font-faces";
  if (document.getElementById(id)) return;
  const css = faces
    .map(
      (f) =>
        `@font-face{font-family:"${f.family}";src:url("${f.url}") format("woff2");` +
        `font-weight:${f.weight ?? 400};font-style:${f.style ?? "normal"};font-display:swap;}`,
    )
    .join("\n");
  const style = document.createElement("style");
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

/** Best-effort: pull the host's first font family from Google Fonts so the iframe
 *  actually has the glyphs (the merchant's own @font-face is not reachable here).
 *  System/keyword families are skipped; an unknown family just 404s and falls back. */
function loadGoogleFont(fontFamily: string): void {
  const first = (fontFamily.split(",")[0] || "").trim().replace(/["']/g, "");
  if (!first) return;
  if (/^(system-ui|ui-sans-serif|ui-serif|sans-serif|serif|monospace|-apple-system|blinkmacsystemfont|"?segoe ui"?|roboto|arial|helvetica|inherit|initial)$/i.test(first)) return;
  const fam = encodeURIComponent(first).replace(/%20/g, "+");
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${fam}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}
